import { useRef, useState } from "react";
import clsx from "clsx";
import domtoimage from "dom-to-image";
import jsPDF from "jspdf";

import { useNavigate, useParams } from "@monta/router-react";
import {
  LoadingSpinner,
  Modal,
  useBreakLG,
  useModal
} from "@monta/design-system";
import AdditionalReport from "#/components/exam-results/additionalReportTab";
import AnswerKey from "#/components/exam-results/answerKey";
import DescriptiveAnswer from "#/components/exam-results/descriptiveAnswerTab";
import layoutStyles from "#/components/exam-results/Layout/styles.module.scss";
import DownloadBtn from "../../DownloadBtn";
import styles from "./styles.module.scss";
import Type2 from "#/modules/exam/result/containers/report-card-type-2";

import type { Tab } from "../../types";

const ExamResultsType2View = () => {
  const navigate = useNavigate();
  const params = useParams();
  const contentRef = useRef<HTMLDivElement>(null);

  const [isPrinting, setIsPrinting] = useState(false);
  const loadingModal = useModal();
  const isDesktop = useBreakLG();
  const tab = params["tabId"] ? +params["tabId"] : 1;
  const examTakingId = Number(params["examTakingId"]);

  const tabs: Tab[] = [
    {
      title: "اطلاعات کلی",
      id: 1,
      component: <Type2 ref={contentRef} isPrint={isPrinting} />
    },
    {
      title: "کارنامه تکمیلی",
      id: 2,
      component: <AdditionalReport />
    },
    {
      title: "پاسخ تشریحی",
      id: 3,
      component: <DescriptiveAnswer />
    },
    {
      title: "کلید و پاسخ",
      id: 4,
      component: <AnswerKey />
    }
  ];

  const renderTabs = () => {
    return tabs.map((item) => (
      <button
        key={`${item.id}btn`}
        className={clsx(
          tab === item.id && layoutStyles["examResultsLayout__tabs--active"]
        )}
        onClick={() =>
          navigate(`/exam-results/type-2/${item.id}/${examTakingId}`)
        }
      >
        {item.title}
      </button>
    ));
  };

  const handlePrint = async () => {
    if (!contentRef.current) return;
    setIsPrinting(true);
    loadingModal.open();

    let originalViewport = "";

    if (!isDesktop) {
      const viewportMeta = document.querySelector(
        'meta[name="viewport"]'
      ) as HTMLMetaElement;
      if (viewportMeta) {
        originalViewport = viewportMeta.content;
        viewportMeta.content = "width=1024";
      }
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Wait for state update and re-render

    const element = contentRef.current;

    try {
      const dataUrl = await domtoimage.toPng(element, {
        width: element.scrollWidth,
        height: element.scrollHeight,
        bgcolor: "#ffffff",
        quality: 1
      });

      const pdf = new jsPDF("p", "mm", "a4", true);
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm

      // Calculate scale to fit the content within A4 page
      const scaleX = imgWidth / element.scrollWidth;
      const scaleY = pageHeight / element.scrollHeight;
      const scale = Math.min(scaleX, scaleY);

      const scaledWidth = element.scrollWidth * scale;
      const scaledHeight = element.scrollHeight * scale;

      pdf.addImage(dataUrl, "PNG", 0, 0, scaledWidth, scaledHeight);

      pdf.save("کارنامه.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      if (!isDesktop) {
        const viewportMeta = document.querySelector(
          'meta[name="viewport"]'
        ) as HTMLMetaElement;
        if (viewportMeta) {
          viewportMeta.content = originalViewport;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsPrinting(false);
      loadingModal.close();
    }
  };

  return (
    <>
      <div className={styles["examResultsType2"]}>
        <div className="lg:flex lg:align-center lg:justify-between lg:px-6 2xl:px-0">
          <div className={layoutStyles["examResultsLayout__tabs"]}>
            {renderTabs()}
          </div>

          {tab === tabs[0].id && <DownloadBtn onClick={handlePrint} />}
        </div>

        <div className={layoutStyles["examResultsLayout__content"]}>
          {(tabs[tab - 1] ?? tabs[0]).component}
        </div>
      </div>

      <Modal
        isOpen={isPrinting}
        open={loadingModal.open}
        close={loadingModal.close}
        height="30vh"
        width="90%"
      >
        <div className="flex items-center justify-center flex-col gap-4 h-full">
          <p className="font-semibold text-3xl text-neutral-800">
            در حال دانلود کارنامه ...
          </p>

          <p className="text-neutral-500 text-2xl">لطفا صبر کنید</p>

          <LoadingSpinner width={48} height={48} color="#475467" />
        </div>
      </Modal>
    </>
  );
};

export default ExamResultsType2View;
